import { useTranslation } from 'react-i18next';

import {
  CERTIFICATE_HEALTH_DESCRIPTION,
  CERTIFICATE_REVIEW_STATUS,
  CERTIFICATE_SOURCES,
} from 'api/documents/documents.model';
import { FormFieldsConfig } from 'components/DocumentForm';
import DatePicker from 'components/Form/DatePicker';
import NumberInput from 'components/Form/InputNumber';
import Select from 'components/Form/Select';
import TextArea from 'components/Form/TextArea';
import {
  ADDITIONAL_DETAILS,
  CHEMICAL_COMPOSITION_DETAILS,
  NESTED_DETAILS,
  PARTICLE_SIZE_DISTRIBUTION_DETAILS,
  PHYSICAL_PROPERTIES_DETAILS,
  PRODUCER_DETAILS,
  SIZE_CALCULATION_DETAILS,
} from 'models/certificate.model';
import { CATEGORY_TYPE } from 'models/documents.model';

export interface CertificateTypeConfig {
  formFields: FormFieldsConfig[];
  uploadPDFLabel?: string;
  isDefault?: boolean;
}

const scoreComponentProps = {
  min: 1,
  max: 6,
  precision: 2,
};

export const useCategoryConfig = (category: CATEGORY_TYPE, productIds?: string[]) => {
  const { t } = useTranslation();

  switch (category) {
    case CATEGORY_TYPE.CERTIFICATE:
    default:
      return {
        formFields: [
          {
            id: 'productId',
            tip: true,
            required: true,
          },
          {
            id: 'productName',
            tip: true,
            required: true,
          },
          {
            id: 'itemId',
            tip: true,
          },
          {
            id: 'batchNumber',
            tip: true,
            required: true,
          },
          {
            id: 'countryOfOrigin',
            tip: true,
            required: true,
          },
          {
            id: 'region',
            tip: true,
          },
          {
            id: 'factory',
            tip: true,
          },
        ],
        isDefault: true,
        uploadPDFLabel: 'additionalCertificate',
      };

    case CATEGORY_TYPE.CHEMISTRY:
      return {
        formFields: [
          {
            id: 'productId',
            label: 'batchNumber(CertificateUID)',
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: 'title',
            label: 'fullName',
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CHEMISTRY_MARKET_NAME,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CHEMICAL_NAME_OR_MATERIAL,
          },
          {
            id: ADDITIONAL_DETAILS.MOLECULAR_FORMULA,
          },
          {
            id: ADDITIONAL_DETAILS.SOURCED,
            component: Select,
            options: Object.values(CERTIFICATE_SOURCES).map((v) => ({
              value: v,
              label: t(v),
            })),
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCTION_DATE,
            component: DatePicker,
          },
          {
            id: 'scoring',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.LITHIUM_PRODUCER,
            nested: [
              {
                id: PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.COBALT_PRODUCER,
            nested: [
              {
                id: PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.CHEMICAL_COMPOSITION,
            nested: Object.values(CHEMICAL_COMPOSITION_DETAILS).map((value) => ({ id: value })),
          },
          {
            id: ADDITIONAL_DETAILS.PHYSICAL_PROPERTIES,
            nested: Object.values(PHYSICAL_PROPERTIES_DETAILS).map((value) => ({ id: value })),
          },
          {
            id: ADDITIONAL_DETAILS.PARTICLE_SIZE_DISTRIBUTION,
            nested: Object.values(PARTICLE_SIZE_DISTRIBUTION_DETAILS).map((value) => ({
              id: value,
            })),
          },
          {
            id: ADDITIONAL_DETAILS.ELECTROCHEMICAL_PERFORMANCE,
            fullWith: true,
            component: TextArea,
          },
          {
            id: 'chemistryProducers',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.LITHIUM_PRODUCER,
            nested: [
              { id: PRODUCER_DETAILS.NO, label: 'no' },
              { id: PRODUCER_DETAILS.COMPANY },
              { id: PRODUCER_DETAILS.COMPANY_HEADQUATERS },
              { id: PRODUCER_DETAILS.MINE },
              { id: PRODUCER_DETAILS.COORDINATES },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.COBALT_PRODUCER,
            nested: [
              { id: PRODUCER_DETAILS.NO, label: 'no' },
              { id: PRODUCER_DETAILS.COMPANY },
              { id: PRODUCER_DETAILS.COMPANY_HEADQUATERS },
              { id: PRODUCER_DETAILS.MINE },
              { id: PRODUCER_DETAILS.COORDINATES },
            ],
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_CELLS:
      return {
        formFields: [
          {
            id: 'productId',
            label: 'batteryCellsBatchNo',
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL, required: true },
              { id: NESTED_DETAILS.CHEMISTRY },
              {
                id: NESTED_DETAILS.CHEMISTRY_BATCH_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
              { id: NESTED_DETAILS.TECHNICAL_SPEC },
              { id: NESTED_DETAILS.CERTIFICATES_OF_CONFORMITY },
              { id: NESTED_DETAILS.TARIC_CODE },
              { id: NESTED_DETAILS.BATTERY_CELL_GLOBAL_TRADE_IDENTIFICATION_NUMBER },
              { id: NESTED_DETAILS.BATTERY_CELL_PRODUCTION_DATE, component: DatePicker },
            ],
          },
          {
            id: 'scoring',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'productScore',
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_CELL_CHILD_LABOR_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: NESTED_DETAILS.BATTERY_CELLS_HUMAN_RIGHTS_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: 'batteryCellProducerScore',
            nested: [
              {
                id: PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: PRODUCER_DETAILS.ESG_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'chemistryScore',
            nested: [
              {
                id: NESTED_DETAILS.MATERIALS_CHILD_LABOR_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: NESTED_DETAILS.MATERIALS_HUMAN_RIGHTS_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'generalInfo',
            nested: [
              { id: NESTED_DETAILS.WEIGHT },
              { id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY_CLEAN },
              { id: NESTED_DETAILS.INTERNAL_RESISTANCE },
              { id: NESTED_DETAILS.NOMINAL_CAPACITY },
              { id: NESTED_DETAILS.AVERAGE_NOMINAL_CAPACITY },
              { id: NESTED_DETAILS.VOLTAGE },
              { id: NESTED_DETAILS.NOMINAL_VOLTAGE },
              { id: NESTED_DETAILS.ENERGY },
              { id: NESTED_DETAILS.FORMAT, label: 'format' },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'operationalInfo',
            nested: [
              { id: NESTED_DETAILS.CHARGING_METHOD },
              { id: NESTED_DETAILS.CHARGING_CURRENT },
              { id: NESTED_DETAILS.CHARGING_TIME },
              { id: NESTED_DETAILS.CHARGING_CYCLES },
              { id: NESTED_DETAILS.DISCHARGE_POWER },
            ],
          },
          {
            id: 'batteryCellProducer',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: null,
            nested: [
              { id: PRODUCER_DETAILS.COMPANY },
              { id: PRODUCER_DETAILS.COMPANY_FULL_NAME },
              { id: PRODUCER_DETAILS.COMPANY_HEADQUATERS },
              { id: PRODUCER_DETAILS.FACTORY_LOCATION },
              { id: PRODUCER_DETAILS.COORDINATES },
            ],
          },
        ],
        uploadPDFLabel: 'techSpecifications',
      };
    case CATEGORY_TYPE.BATTERY_PACKS:
      return {
        formFields: [
          {
            id: 'productId',
            label: 'batteryPackSerialNumber(CertificateUID)',
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.EVMODEL, required: true },
              { id: NESTED_DETAILS.BATTERY_PRODUCERS },
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL },
              { id: NESTED_DETAILS.BATTERY_PRODUCTION_DATE, component: DatePicker },
              { id: NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER },
              {
                id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
            ],
          },
          {
            id: 'scoring',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'productScore',
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_PACK_CHILD_LABOR_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: NESTED_DETAILS.BATTERY_PACK_HUMAN_RIGHTS_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: NESTED_DETAILS.BATTERY_PACK_ESG_SCORING_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: 'batteryPackProducerScore',
            nested: [
              {
                id: PRODUCER_DETAILS.CHILD_LABOR_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: PRODUCER_DETAILS.HUMAN_RIGHTS_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: PRODUCER_DETAILS.ESG_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'batteryCellsScore',
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_CELLS_CHILD_LABOR_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: NESTED_DETAILS.BATTERY_CELLS_HUMAN_RIGHTS_AVERAGE,
                component: NumberInput,
                ...scoreComponentProps,
              },
              {
                id: NESTED_DETAILS.BATTERY_CELL_PRODUCER_ESG_SCORING,
                component: NumberInput,
                ...scoreComponentProps,
              },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: 'generalInfo',
            nested: [
              { id: NESTED_DETAILS.WEIGHT },
              {
                id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
                label: 'gravimetricEnergyDensity',
              },
              { id: NESTED_DETAILS.COOLING },
              { id: NESTED_DETAILS.CELLS_SETUP },
              { id: NESTED_DETAILS.TOTAL_CAPACITY },
              { id: NESTED_DETAILS.USABLE_CAPACITY },
              { id: NESTED_DETAILS.STORAGE_TEMP_RANGE },
              { id: NESTED_DETAILS.WARRANTY_CONDITIONS },
            ],
          },
          {
            id: ADDITIONAL_DETAILS.SIZE_CALCULATION,
            label: 'relevantNumbers',
            nested: Object.values(SIZE_CALCULATION_DETAILS).map((value) => ({ id: value })),
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [{ id: NESTED_DETAILS.DESCRIPTION, fullWith: true, component: TextArea }],
          },
          {
            id: 'batteryPackProducer',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: null,
            nested: [
              { id: PRODUCER_DETAILS.COMPANY_FULL_NAME },
              { id: PRODUCER_DETAILS.COMPANY_HEADQUATERS },
              { id: PRODUCER_DETAILS.FACTORY_LOCATION },
              { id: PRODUCER_DETAILS.COORDINATES },
            ],
          },
        ],
        uploadPDFLabel: 'userManual',
      };
    case CATEGORY_TYPE.BATTERY_TECH_REVIEW:
      return {
        formFields: [
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
              { id: NESTED_DETAILS.BATTERY_PRODUCERS },
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL },
              { id: NESTED_DETAILS.BATTERY_PRODUCTION_DATE, component: DatePicker },
              { id: NESTED_DETAILS.EVMODEL, required: true },
            ],
          },
          {
            id: 'technicalReviewInformation',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              {
                id: NESTED_DETAILS.TECHNICAL_REVIEW_STATUS,
                component: Select,
                options: Object.values(CERTIFICATE_REVIEW_STATUS).map((v) => ({
                  value: v,
                  label: t(v),
                })),
              },
              { id: NESTED_DETAILS.SALE_DATE, component: DatePicker },
              { id: NESTED_DETAILS.TECHNICAL_REVIEW_DATE, component: DatePicker },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.WEIGHT },
              {
                id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
                label: 'gravimetricEnergyDensity',
              },
              { id: NESTED_DETAILS.COOLING },
              { id: NESTED_DETAILS.STORAGE_TEMP_RANGE },
              { id: NESTED_DETAILS.CELLS_SETUP },
              { id: NESTED_DETAILS.TOTAL_CAPACITY },
              { id: NESTED_DETAILS.USABLE_CAPACITY },
              { id: NESTED_DETAILS.DESCRIPTION, fullWith: true, component: TextArea },
            ],
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_PACKS_REPAIR:
      return {
        formFields: [
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
              { id: NESTED_DETAILS.BATTERY_PRODUCERS },
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL },
              { id: NESTED_DETAILS.BATTERY_PRODUCTION_DATE, component: DatePicker },
              { id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER },
              { id: NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER },
              { id: NESTED_DETAILS.EVMODEL, required: true },
            ],
          },
          {
            id: 'repairInfo',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.REPAIR_DESCRIPTION },
              { id: NESTED_DETAILS.REPAIR_DATE, component: DatePicker },
              {
                id: NESTED_DETAILS.HEALTH_DESCRIPTION,
                component: Select,
                options: Object.values(CERTIFICATE_HEALTH_DESCRIPTION).map((v) => ({
                  value: v,
                  label: t(v),
                })),
              },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.CELLS_SETUP },
              { id: NESTED_DETAILS.TOTAL_CAPACITY },
              { id: NESTED_DETAILS.USABLE_CAPACITY },
              { id: NESTED_DETAILS.WEIGHT },
              { id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY_CLEAN },
              { id: NESTED_DETAILS.COOLING },
              { id: NESTED_DETAILS.STORAGE_TEMP_RANGE },
              { id: NESTED_DETAILS.DESCRIPTION, fullWith: true, component: TextArea },
            ],
          },
          {
            id: 'repairCompany',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: null,
            nested: [
              { id: PRODUCER_DETAILS.COMPANY, label: 'repairCompanyFullName' },
              { id: PRODUCER_DETAILS.FACTORY_LOCATION, label: 'repairLocation' },
            ],
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_REPURPOSE:
      return {
        formFields: [
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
              { id: NESTED_DETAILS.BATTERY_PRODUCERS },
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL },
              { id: NESTED_DETAILS.BATTERY_PRODUCTION_DATE, component: DatePicker },
              { id: NESTED_DETAILS.EVMODEL, required: true },
            ],
          },
          {
            id: 'repurpose',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.REPURPOSE_DATE, component: DatePicker },
              { id: NESTED_DETAILS.TECHNICAL_REVIEW_DATE, component: DatePicker },
              { id: NESTED_DETAILS.REMANUFACTURING_TYPE },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.CELLS_SETUP },
              { id: NESTED_DETAILS.TOTAL_CAPACITY },
              { id: NESTED_DETAILS.USABLE_CAPACITY },
              { id: NESTED_DETAILS.WEIGHT },
              { id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY_CLEAN },
            ],
          },
          {
            id: 'repurposeCompany',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: null,
            nested: [
              { id: PRODUCER_DETAILS.COMPANY },
              { id: PRODUCER_DETAILS.FACTORY_LOCATION, label: 'repurposeAddress' },
              { id: PRODUCER_DETAILS.COORDINATES, label: 'geoLocation' },
            ],
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_PACKS_RECYCLING:
      return {
        formFields: [
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
              { id: NESTED_DETAILS.BATTERY_PRODUCTION_DATE, component: DatePicker },
              { id: NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER },
              { id: NESTED_DETAILS.BATTERY_PACKS_RECYCLING },
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL, required: true },
              { id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER },
              { id: NESTED_DETAILS.BATTERY_PRODUCERS },
              { id: NESTED_DETAILS.EVMODEL },
            ],
          },
          {
            id: 'recyclingDescription',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.RECYCLE_DATA, component: DatePicker },
              { id: NESTED_DETAILS.RECYCLE_PERCENT, component: NumberInput, min: 0, max: 100 },
              { id: NESTED_DETAILS.RECYCLE_MATERIAL },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.CELLS_SETUP },
              { id: NESTED_DETAILS.WEIGHT },
              {
                id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
                label: 'gravimetricEnergyDensity',
              },
            ],
          },
          {
            id: 'recyclingCompany',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: null,
            nested: [
              { id: PRODUCER_DETAILS.COMPANY, label: 'recycler' },
              { id: PRODUCER_DETAILS.COMPANY_HEADQUATERS },
              { id: PRODUCER_DETAILS.RECYCLER_LOCATION },
            ],
          },
        ],
      };
    case CATEGORY_TYPE.BATTERY_WASTE:
      return {
        formFields: [
          {
            id: ADDITIONAL_DETAILS.ASSEMBLY_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.CONTROL_NUMBER,
            required: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              {
                id: NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
                required: true,
                component: Select,
                options: productIds?.map((productId) => ({
                  value: productId,
                  label: productId,
                })),
              },
              { id: NESTED_DETAILS.BATTERY_PRODUCTION_DATE, component: DatePicker },
              { id: NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER },
              { id: NESTED_DETAILS.BATTERY_PRODUCERS },
              { id: NESTED_DETAILS.BATTERY_CELL_MODEL, required: true },
              { id: NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER },
              { id: NESTED_DETAILS.EVMODEL },
            ],
          },
          {
            id: 'wasteDescription',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.RECYCLE_DATA, component: DatePicker },
              {
                id: NESTED_DETAILS.OF_WASTES,
                component: NumberInput,
                min: 0,
                max: 100,
              },
              { id: NESTED_DETAILS.WASTE_WEIGHT },
            ],
          },
          {
            id: 'specification',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.DETAILS,
            label: null,
            nested: [
              { id: NESTED_DETAILS.CELLS_SETUP },
              { id: NESTED_DETAILS.WEIGHT },
              {
                id: NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
                label: 'gravimetricEnergyDensity',
              },
            ],
          },
          {
            id: 'wasteCompany',
            isSectionTitle: true,
          },
          {
            id: ADDITIONAL_DETAILS.PRODUCER,
            label: null,
            nested: [
              { id: PRODUCER_DETAILS.COMPANY },
              { id: PRODUCER_DETAILS.FACTORY_LOCATION, label: 'address' },
              { id: PRODUCER_DETAILS.COORDINATES, label: 'geoLocation' },
            ],
          },
        ],
      };
  }
};
